import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, History, Image as ImageIcon, ImagePlus, Lightbulb, Loader2, Sparkles, UploadCloud, Video, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { StepIndicator } from "@/components/create/StepIndicator";
import { ConsentModal } from "@/components/create/ConsentModal";
import { findTemplate, type Template } from "@/lib/templates";
import { RecreateFromHistoryModal } from "@/components/RecreateFromHistoryModal";

const videoSearchSchema = z.object({
  template: fallback(z.string().optional(), undefined).optional(),
  mode: fallback(z.enum(["faceswap", "i2v", "story"]).optional(), undefined).optional(),
  from: fallback(z.string().optional(), undefined).optional(),
  fromImage: fallback(z.string().optional(), undefined).optional(),
});

export const Route = createFileRoute("/_authenticated/create/video")({
  head: () => ({
    meta: [
      { title: "動画を作る | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: zodValidator(videoSearchSchema),
  component: CreateUploadPage,
});

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const IMAGE_MAX = 10 * 1024 * 1024; // 10MB
const BG_MAX = 15 * 1024 * 1024; // 15MB
const VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const VIDEO_MAX = 100 * 1024 * 1024; // 100MB
const VIDEO_MAX_DURATION = 30; // seconds

const STORAGE_KEY = "create:wizard";

type UploadedAsset = {
  path: string;
  signedUrl: string;
  name: string;
  size: number;
};

type VideoAsset = UploadedAsset & {
  durationSec: number;
  thumbnailPath?: string;
  thumbnailSignedUrl?: string;
};

export type CreateMode = "faceswap" | "i2v" | "story";

export type StoryShot = { prompt: string; duration: number };

type WizardState = {
  mode?: CreateMode; // default "faceswap"
  image?: UploadedAsset;
  background?: UploadedAsset;
  video?: VideoAsset;
  templateId?: string;
  templatePrompt?: string;
  videoPrompt?: string; // i2v 用
  storyShots?: StoryShot[]; // story 用
};

function readWizardState(): WizardState {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeWizardState(s: WizardState) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function formatBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

async function getVideoMetadata(
  file: File,
): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const meta = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      };
      window.URL.revokeObjectURL(video.src);
      resolve(meta);
    };
    video.onerror = () => reject(new Error("動画の読み込みに失敗しました"));
    video.src = URL.createObjectURL(file);
  });
}

// Extract a thumbnail at ~0.1s as a JPEG Blob.
async function extractVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    let settled = false;
    const finish = (b: Blob | null) => {
      if (settled) return;
      settled = true;
      try {
        URL.revokeObjectURL(video.src);
      } catch {
        /* noop */
      }
      resolve(b);
    };
    video.onloadeddata = () => {
      try {
        video.currentTime = Math.min(0.1, Math.max(0, (video.duration || 1) - 0.05));
      } catch {
        finish(null);
      }
    };
    video.onseeked = () => {
      try {
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return finish(null);
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => finish(blob), "image/jpeg", 0.85);
      } catch {
        finish(null);
      }
    };
    video.onerror = () => finish(null);
    setTimeout(() => finish(null), 8000);
    video.src = URL.createObjectURL(file);
  });
}

/**
 * 保存済みの uploads バケットURL（署名URL/公開URL）からストレージパスを抽出する。
 * 例: https://x.supabase.co/storage/v1/object/sign/uploads/<userId>/<file>?token=...
 *     → "<userId>/<file>"
 */
function extractUploadsPath(url: string): string | null {
  try {
    const u = new URL(url);
    const marker = "/storage/v1/object/";
    const idx = u.pathname.indexOf(marker);
    if (idx === -1) return null;
    const rest = u.pathname.substring(idx + marker.length);
    const parts = rest.split("/");
    // parts[0] = sign|public|authenticated, parts[1] = bucket
    if (parts.length < 3 || parts[1] !== "uploads") return null;
    return parts.slice(2).map(decodeURIComponent).join("/");
  } catch {
    return null;
  }
}

/**
 * 保存済みURLの署名が切れていても確実に取得できるよう、
 * uploads バケットのパスが取れる場合は新しい署名URLを発行して返す。
 */
async function resolveFetchableUrl(url: string): Promise<string> {
  const path = extractUploadsPath(url);
  if (!path) return url; // 外部URL（テンプレ素材など）はそのまま
  const { data } = await supabase.storage
    .from("uploads")
    .createSignedUrl(path, 60 * 5);
  return data?.signedUrl ?? url;
}

async function uploadFromUrl(
  userId: string,
  url: string,
  filenameHint: string,
  expectedKind?: "image" | "video",
): Promise<UploadedAsset> {
  const fetchUrl = await resolveFetchableUrl(url);
  const res = await fetch(fetchUrl);
  if (!res.ok) throw new Error(`素材の取得に失敗 (${res.status})`);
  const blob = await res.blob();
  if (expectedKind === "image" && blob.type && !blob.type.startsWith("image/")) {
    throw new Error(`期待した画像ではありません (${blob.type})`);
  }
  if (expectedKind === "video" && blob.type && !blob.type.startsWith("video/")) {
    throw new Error(`期待した動画ではありません (${blob.type})`);
  }
  const path = `${userId}/${Date.now()}-tpl-${filenameHint}`;
  const { error } = await supabase.storage
    .from("uploads")
    .upload(path, blob, { contentType: blob.type, upsert: false });
  if (error) throw error;
  const { data: signed } = await supabase.storage
    .from("uploads")
    .createSignedUrl(path, 60 * 60 * 24);
  return {
    path,
    signedUrl: signed?.signedUrl ?? "",
    name: filenameHint,
    size: blob.size,
  };
}

function CreateUploadPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [userId, setUserId] = useState<string | null>(null);
  const [state, setState] = useState<WizardState>({});
  // null = 未読込, true = 既同意, false = 要モーダル
  const [verified, setVerified] = useState<boolean | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [prefilling, setPrefilling] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id ?? null;
      if (cancelled) return;
      setUserId(uid);
      if (!uid) {
        setVerified(true); // 未ログインは別ガードに任せる
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("verified")
        .eq("user_id", uid)
        .maybeSingle();
      if (!cancelled) setVerified(Boolean(profile?.verified));
    })();
    setState(readWizardState());
    return () => {
      cancelled = true;
    };
  }, []);

  const updateState = useCallback((next: WizardState) => {
    setState(next);
    writeWizardState(next);
  }, []);

  // ?mode= で来たらモードを反映
  useEffect(() => {
    if (search.mode && search.mode !== state.mode) {
      updateState({ ...state, mode: search.mode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.mode]);

  const mode: CreateMode = state.mode ?? "faceswap";

  // テンプレートからの自動配置
  useEffect(() => {
    const tpl = findTemplate(search.template);
    if (!tpl) return;
    setTemplate(tpl);
    if (!userId) return;
    // 既に同じテンプレで配置済みならスキップ
    if (state.templateId === tpl.id && (state.background || state.video)) return;
    let cancelled = false;
    (async () => {
      setPrefilling(true);
      const next: WizardState = {
        ...state,
        mode: tpl.mode,
        templateId: tpl.id,
        templatePrompt: tpl.prompt,
      };
      try {
        if (tpl.backgroundImage && !next.background) {
          try {
            next.background = await uploadFromUrl(
              userId,
              tpl.backgroundImage,
              `background-${tpl.id}.jpg`,
            );
          } catch (e) {
            console.warn("template background prefill skipped:", e);
          }
        }
        if (tpl.mode === "faceswap" && tpl.baseVideo && !next.video) {
          try {
            const base = await uploadFromUrl(
              userId,
              tpl.baseVideo,
              `base-${tpl.id}.mp4`,
            );
            next.video = { ...base, durationSec: 0 };
          } catch (e) {
            console.warn("template base video prefill skipped:", e);
          }
        }
      } finally {
        if (!cancelled) {
          updateState(next);
          setPrefilling(false);
          if (next.background || next.video) {
            toast.success(`「${tpl.title}」テンプレートの素材を配置しました`);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.template, userId]);

  const applyFromGeneration = useCallback(
    async (genId: string) => {
      if (!userId) {
        toast.error("認証情報を確認できませんでした");
        return;
      }
      setPrefilling(true);
      try {
        const { data, error } = await supabase
          .from("generations")
          .select(
            "id, mode, input_image_url, input_video_url, background_url, video_prompt, prompt_original",
          )
          .eq("id", genId)
          .maybeSingle();
        if (error || !data) {
          toast.error("過去の生成を読み込めませんでした");
          return;
        }
        const next: WizardState = { ...readWizardState() };
        const m = (data.mode as CreateMode | null) ?? "faceswap";
        next.mode = m === "story" ? "faceswap" : m;
        const vp = (data.video_prompt ?? data.prompt_original ?? "") as string;
        if (vp) next.videoPrompt = vp.slice(0, 2500);
        const failures: string[] = [];
        // 同一URLが画像と動画の両方に入っているケース（古い不整合データ）はスキップ
        const sameUrl = !!data.input_image_url && data.input_image_url === data.input_video_url;
        const imagePromise = data.input_image_url && !sameUrl
          ? uploadFromUrl(userId, data.input_image_url, `recreate-image-${genId}.jpg`, "image")
              .then((v) => ({ ok: true as const, v }))
              .catch((e) => {
                console.warn("recreate image asset error", e);
                return { ok: false as const, label: "画像" };
              })
          : Promise.resolve(null);
        const bgPromise = data.background_url
          ? uploadFromUrl(userId, data.background_url, `recreate-background-${genId}.jpg`, "image")
              .then((v) => ({ ok: true as const, v }))
              .catch((e) => {
                console.warn("recreate background asset error", e);
                return { ok: false as const, label: "背景" };
              })
          : Promise.resolve(null);
        const videoPromise = m === "faceswap" && data.input_video_url
          ? uploadFromUrl(userId, data.input_video_url, `recreate-video-${genId}.mp4`, "video")
              .then((v) => ({ ok: true as const, v }))
              .catch((e) => {
                console.warn("recreate video asset error", e);
                return { ok: false as const, label: "動画" };
              })
          : Promise.resolve(null);

        const [imgRes, bgRes, vidRes] = await Promise.all([imagePromise, bgPromise, videoPromise]);
        if (imgRes) {
          if (imgRes.ok) next.image = imgRes.v;
          else failures.push(imgRes.label);
        }
        if (bgRes) {
          if (bgRes.ok) next.background = bgRes.v;
          else failures.push(bgRes.label);
        }
        if (vidRes) {
          if (vidRes.ok) next.video = { ...vidRes.v, durationSec: 0 };
          else failures.push(vidRes.label);
        }
        if (sameUrl) failures.push("画像");
        // clear template-specific state to avoid mixing
        next.templateId = undefined;
        next.templatePrompt = undefined;
        updateState(next);
        if (failures.length > 0) {
          toast.warning(
            `${failures.join("・")}の復元に失敗しました。再アップロードしてください`,
          );
        } else {
          toast.success("過去の設定を読み込みました");
        }
      } finally {
        setPrefilling(false);
      }
    },
    [userId, updateState],
  );

  // ?from={id} で来たら自動 pre-fill
  useEffect(() => {
    if (!search.from || !userId) return;
    const id = search.from;
    void (async () => {
      await applyFromGeneration(id);
      navigate({
        to: "/create/video",
        search: {} as never,
        replace: true,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.from, userId]);

  // ?fromImage={imageGenId} で来たら output_image_url を素材として配置 (i2v)
  useEffect(() => {
    if (!search.fromImage || !userId) return;
    const genId = search.fromImage;
    void (async () => {
      setPrefilling(true);
      try {
        const { data, error } = await supabase
          .from("generations")
          .select("id, output_image_url, prompt_original")
          .eq("id", genId)
          .maybeSingle();
        if (error || !data?.output_image_url) {
          toast.error("画像を読み込めませんでした");
          return;
        }
        const asset = await uploadFromUrl(
          userId,
          data.output_image_url,
          `from-image-${genId}.jpg`,
          "image",
        );
        const next: WizardState = { ...readWizardState() };
        next.mode = "i2v";
        next.image = asset;
        next.video = undefined;
        next.templateId = undefined;
        next.templatePrompt = undefined;
        updateState(next);
        toast.success("画像を素材として配置しました");
      } catch (e) {
        console.warn("fromImage error", e);
        toast.error("画像の取り込みに失敗しました");
      } finally {
        setPrefilling(false);
        navigate({ to: "/create/video", search: { mode: "i2v" } as never, replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.fromImage, userId]);


  const storyShots = state.storyShots ?? [{ prompt: "", duration: 5 }];
  const storyValid = mode !== "story" || (
    storyShots.length >= 1 && storyShots.length <= 5 &&
    storyShots.every((s) => s.prompt.trim().length > 0 && s.duration >= 1 && s.duration <= 12)
  );

  const canContinue = mode === "faceswap"
    ? !!state.image && !!state.video
    : mode === "story"
      ? !!state.image && storyValid
      : !!state.image && !!(state.videoPrompt ?? state.templatePrompt ?? "").trim();

  const handleNext = () => {
    if (!canContinue) return;
    navigate({ to: "/create/quality" });
  };

  return (
    <>
      <ConsentModal
        open={verified === false}
        onAgreed={() => setVerified(true)}
        onCancel={() => navigate({ to: "/dashboard" })}
      />
      {prefilling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm">
          <div className="mx-4 flex max-w-sm flex-col items-center gap-4 rounded-2xl border border-border bg-card px-8 py-10 text-center shadow-xl">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <Sparkles className="absolute inset-0 m-auto h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-base font-bold">素材を復元しています</p>
              <p className="text-xs text-muted-foreground">
                過去の動画から画像・背景・ベース動画を取得中です。<br />
                通常 10〜30 秒ほどかかります。
              </p>
            </div>
          </div>
        </div>
      )}
      <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto max-w-6xl">
        <Link
          to="/create"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          新規作成に戻る
        </Link>
        <div className="mb-10">
          <StepIndicator current={1} />
        </div>

        <header className="mb-6 text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">素材をアップロード</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "faceswap"
              ? "入れ替えたい人物の画像と、ベースとなる動画を選んでください。背景画像は任意です。"
              : mode === "story"
                ? "メインとなる人物・キャラクター画像をアップロードして、各シーンのプロンプトを入力します。"
                : "動かしたい画像をアップロードして、次の画面でプロンプトを入力します。"}
          </p>
        </header>

        <div className="mb-6 flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            disabled={prefilling}
          >
            <History className="mr-2 h-4 w-4" />
            過去の生成から
          </Button>
        </div>

        {/* モード選択 */}
        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-full border border-border bg-card p-1 shadow-sm">
            <button
              type="button"
              onClick={() => updateState({ ...state, mode: "faceswap" })}
              className={cn(
                "rounded-full px-4 py-2 text-xs font-semibold transition sm:text-sm",
                mode === "faceswap"
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              ベース動画を使う
            </button>
            <button
              type="button"
              onClick={() => updateState({ ...state, mode: "i2v" })}
              className={cn(
                "rounded-full px-4 py-2 text-xs font-semibold transition sm:text-sm",
                mode === "i2v"
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              プロンプトで動かす
            </button>
            {/* ストーリーリールは未実装のため一時的に非表示
            <button
              type="button"
              onClick={() => updateState({ ...state, mode: "story" })}
              className={cn(
                "rounded-full px-4 py-2 text-xs font-semibold transition sm:text-sm",
                mode === "story"
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              ストーリーリール
            </button>
            */}
          </div>
        </div>

        {template && (
          <div className="mb-6 rounded-xl border border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                {prefilling ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-primary">テンプレート</p>
                  <p className="text-sm font-bold">{template.title}</p>
                  <span className="rounded-full bg-background/60 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    {template.mode === "faceswap" ? "顔差し替え" : "プロンプト生成"}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {prefilling
                    ? "おすすめ素材を準備中..."
                    : template.mode === "faceswap"
                      ? "おすすめの背景・ベース動画を配置しました。必要に応じて差し替えてください。"
                      : "おすすめの背景を配置しました。次の画面で推奨プロンプトが入ります。"}
                </p>
                {template.prompt && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-md bg-background/60 p-2 text-xs">
                    <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                    <span className="text-muted-foreground">
                      推奨プロンプト: <span className="text-foreground">{template.prompt}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AIで素材を作る導線 */}
        <Link
          to="/create/image"
          className="group mb-8 block rounded-xl border border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 transition hover:border-primary hover:shadow-md sm:p-5"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Wand2 className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-bold sm:text-lg">素材がない？AIで画像を作る</h2>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
                  NEW
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                プロンプトを入力するだけで素材画像を生成。そのまま動画化もできます。
              </p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        <div
          className={cn(
            "grid grid-cols-1 gap-6",
            mode === "faceswap" ? "md:grid-cols-3" : "md:grid-cols-2",
          )}
        >
          <ImageUploader
            userId={userId}
            value={state.image}
            onChange={(image) => updateState({ ...state, image })}
          />
          <BackgroundUploader
            userId={userId}
            value={state.background}
            onChange={(background) => updateState({ ...state, background })}
          />
          {mode === "faceswap" && (
            <VideoUploader
              userId={userId}
              value={state.video}
              onChange={(video) => updateState({ ...state, video })}
            />
          )}
        </div>

        {mode === "i2v" && (
          <div className="mt-6">
            <label htmlFor="video-prompt" className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4 text-primary" />
              動かし方のプロンプト
              <span className="text-xs font-normal text-muted-foreground">（必須）</span>
            </label>
            <textarea
              id="video-prompt"
              value={state.videoPrompt ?? state.templatePrompt ?? ""}
              onChange={(e) =>
                updateState({ ...state, videoPrompt: e.target.value.slice(0, 2500) })
              }
              placeholder="例：人物がゆっくり振り向いて微笑む、髪が風になびく"
              maxLength={2500}
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>画像をどう動かすか具体的に書くほど精度が上がります</span>
              <span>{(state.videoPrompt ?? state.templatePrompt ?? "").length} / 2500</span>
            </div>
          </div>
        )}

        {mode === "story" && (
          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4 text-primary" />
                シーン構成（{storyShots.length} / 5）
                <span className="text-xs font-normal text-muted-foreground">（最大5シーン）</span>
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={storyShots.length <= 1}
                  onClick={() =>
                    updateState({ ...state, storyShots: storyShots.slice(0, -1) })
                  }
                >
                  − シーン削除
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={storyShots.length >= 5}
                  onClick={() =>
                    updateState({
                      ...state,
                      storyShots: [...storyShots, { prompt: "", duration: 5 }],
                    })
                  }
                >
                  ＋ シーン追加
                </Button>
              </div>
            </div>
            {storyShots.map((shot, i) => (
              <div key={i} className="rounded-md border border-border bg-card p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-primary">シーン {i + 1}</span>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    秒数
                    <select
                      value={shot.duration}
                      onChange={(e) => {
                        const next = [...storyShots];
                        next[i] = { ...shot, duration: Number(e.target.value) };
                        updateState({ ...state, storyShots: next });
                      }}
                      className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                    >
                      {[3, 4, 5, 6, 7, 8, 10, 12].map((d) => (
                        <option key={d} value={d}>{d}s</option>
                      ))}
                    </select>
                  </label>
                </div>
                <textarea
                  value={shot.prompt}
                  onChange={(e) => {
                    const next = [...storyShots];
                    next[i] = { ...shot, prompt: e.target.value.slice(0, 512) };
                    updateState({ ...state, storyShots: next });
                  }}
                  placeholder={`例：${i === 0 ? "人物が振り向き微笑む、ソフトな朝の光" : "別アングル、カメラがゆっくり寄る、夕暮れの街並み"}`}
                  maxLength={512}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <div className="mt-1 text-right text-xs text-muted-foreground">
                  {shot.prompt.length} / 512
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              合計時間: {storyShots.reduce((a, s) => a + s.duration, 0)}秒 ／
              アスペクト比: 9:16（縦動画リール）
            </p>
          </div>
        )}

        <div className="mt-10 flex flex-col items-center gap-4">
          <Button
            size="lg"
            onClick={handleNext}
            disabled={!canContinue}
            className={cn("h-12 px-10 text-base font-semibold", canContinue && "bg-gradient-brand")}
          >
            次へ：品質を選択
            <ArrowRight className="h-4 w-4" />
          </Button>

          <div className="mt-2 max-w-xl space-y-1 text-center text-xs text-muted-foreground">
            <p>※ 第三者の画像を本人の同意なくアップロードすることは禁止されています</p>
            <p>※ 未成年が映る動画は使用できません</p>
          </div>
        </div>
      </div>
    </main>
    <RecreateFromHistoryModal
      open={historyOpen}
      onOpenChange={setHistoryOpen}
      kind="video"
      onPick={(id) => void applyFromGeneration(id)}
    />
    </>
  );
}

// ---------- Image Uploader ----------

function ImageUploader({
  userId,
  value,
  onChange,
}: {
  userId: string | null;
  value?: UploadedAsset;
  onChange: (v: UploadedAsset | undefined) => void;
}) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const validate = (file: File): string | null => {
    if (!IMAGE_TYPES.includes(file.type)) return "JPG / PNG / WebP 形式のみ対応しています";
    if (file.size > IMAGE_MAX) return "ファイルサイズが10MBを超えています";
    return null;
  };

  const handleFile = async (file: File) => {
    if (!userId) {
      toast.error("認証情報を確認できませんでした");
      return;
    }
    const err = validate(file);
    if (err) {
      toast.error(err);
      return;
    }
    setUploading(true);
    setProgress(10);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${userId}/${Date.now()}-image.${ext}`;
      const tick = setInterval(() => setProgress((p) => Math.min(90, p + 10)), 200);
      const { error } = await supabase.storage
        .from("uploads")
        .upload(path, file, { contentType: file.type, upsert: false });
      clearInterval(tick);
      if (error) throw error;
      const { data: signed } = await supabase.storage
        .from("uploads")
        .createSignedUrl(path, 60 * 60 * 24);
      setProgress(100);
      onChange({
        path,
        signedUrl: signed?.signedUrl ?? "",
        name: file.name,
        size: file.size,
      });
      toast.success("画像をアップロードしました");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const remove = async () => {
    if (value?.path) {
      await supabase.storage.from("uploads").remove([value.path]);
    }
    onChange(undefined);
  };

  return (
    <UploaderShell
      titleFixed="入れ替えたい人物の画像"
      hint="顔がはっきり写っている正面写真を推奨"
      formatHint="JPG / PNG / WebP・最大10MB"
      icon={<ImageIcon className="h-6 w-6" />}
      uploading={uploading}
      progress={progress}
      hasValue={!!value}
      accept={IMAGE_TYPES.join(",")}
      onFile={handleFile}
      preview={
        value ? (
          <div className="relative">
            <img
              src={value.signedUrl}
              alt={value.name}
              className="aspect-square w-full rounded-lg object-cover"
            />
            <RemoveButton onClick={remove} />
            <FileMeta name={value.name} size={value.size} />
          </div>
        ) : null
      }
    />
  );
}

// ---------- Background Uploader (optional) ----------

function BackgroundUploader({
  userId,
  value,
  onChange,
}: {
  userId: string | null;
  value?: UploadedAsset;
  onChange: (v: UploadedAsset | undefined) => void;
}) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const validate = (file: File): string | null => {
    if (!IMAGE_TYPES.includes(file.type)) return "JPG / PNG / WebP 形式のみ対応しています";
    if (file.size > BG_MAX) return "ファイルサイズが15MBを超えています";
    return null;
  };

  const handleFile = async (file: File) => {
    if (!userId) {
      toast.error("認証情報を確認できませんでした");
      return;
    }
    const err = validate(file);
    if (err) {
      toast.error(err);
      return;
    }
    setUploading(true);
    setProgress(10);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${userId}/${Date.now()}-background.${ext}`;
      const tick = setInterval(() => setProgress((p) => Math.min(90, p + 10)), 200);
      const { error } = await supabase.storage
        .from("uploads")
        .upload(path, file, { contentType: file.type, upsert: false });
      clearInterval(tick);
      if (error) throw error;
      const { data: signed } = await supabase.storage
        .from("uploads")
        .createSignedUrl(path, 60 * 60 * 24);
      setProgress(100);
      onChange({
        path,
        signedUrl: signed?.signedUrl ?? "",
        name: file.name,
        size: file.size,
      });
      toast.success("背景画像をアップロードしました");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const remove = async () => {
    if (value?.path) {
      await supabase.storage.from("uploads").remove([value.path]);
    }
    onChange(undefined);
  };

  return (
    <UploaderShell
      titleFixed="背景画像（任意）"
      hint="差し替えたい背景画像があればアップロード"
      formatHint="JPG / PNG / WebP・最大15MB"
      icon={<ImagePlus className="h-6 w-6" />}
      uploading={uploading}
      progress={progress}
      hasValue={!!value}
      accept={IMAGE_TYPES.join(",")}
      onFile={handleFile}
      preview={
        value ? (
          <div className="relative">
            <img
              src={value.signedUrl}
              alt={value.name}
              className="aspect-square w-full rounded-lg object-cover"
            />
            <RemoveButton onClick={remove} />
            <FileMeta name={value.name} size={value.size} />
          </div>
        ) : null
      }
    />
  );
}

// ---------- Video Uploader ----------

function VideoUploader({
  userId,
  value,
  onChange,
}: {
  userId: string | null;
  value?: VideoAsset;
  onChange: (v: VideoAsset | undefined) => void;
}) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!userId) {
      toast.error("認証情報を確認できませんでした");
      return;
    }
    if (!VIDEO_TYPES.includes(file.type)) {
      toast.error("MP4 / MOV 形式のみ対応しています");
      return;
    }
    if (file.size > VIDEO_MAX) {
      toast.error("ファイルサイズが100MBを超えています");
      return;
    }

    setUploading(true);
    setProgress(5);
    try {
      const { duration, width, height } = await getVideoMetadata(file);
      if (duration > VIDEO_MAX_DURATION + 0.5) {
        toast.error(
          `動画の長さが${VIDEO_MAX_DURATION}秒を超えています(${duration.toFixed(1)}秒)`,
        );
        setUploading(false);
        setProgress(0);
        return;
      }
      const minSide = Math.min(width, height);
      if (minSide < 700) {
        toast.error(
          `動画の解像度が小さすぎます（短辺 ${minSide}px / 700px 以上が必要）。スマホのカメラで直接撮影した動画をご利用ください。LINE や SNS 経由で受け取った動画は圧縮されていることがあります。`,
          { duration: 8000 },
        );
        setUploading(false);
        setProgress(0);
        return;
      }

      // Extract thumbnail in parallel with video upload
      const thumbnailBlob = await extractVideoThumbnail(file);

      const ext = file.name.split(".").pop() || "mp4";
      const ts = Date.now();
      const path = `${userId}/${ts}-video.${ext}`;
      const thumbPath = thumbnailBlob ? `${userId}/${ts}-thumbnail.jpg` : undefined;

      const tick = setInterval(() => setProgress((p) => Math.min(90, p + 5)), 250);
      const uploadVideo = supabase.storage
        .from("uploads")
        .upload(path, file, { contentType: file.type, upsert: false });
      const uploadThumb = thumbnailBlob && thumbPath
        ? supabase.storage
            .from("uploads")
            .upload(thumbPath, thumbnailBlob, {
              contentType: "image/jpeg",
              upsert: false,
            })
        : Promise.resolve({ error: null });

      const [{ error: vErr }, { error: tErr }] = await Promise.all([
        uploadVideo,
        uploadThumb,
      ]);
      clearInterval(tick);
      if (vErr) throw vErr;
      if (tErr) {
        console.warn("thumbnail upload failed", tErr);
      }

      const [{ data: signed }, thumbSignedRes] = await Promise.all([
        supabase.storage.from("uploads").createSignedUrl(path, 60 * 60 * 24),
        thumbPath && !tErr
          ? supabase.storage.from("uploads").createSignedUrl(thumbPath, 60 * 60 * 24)
          : Promise.resolve({ data: null }),
      ]);

      setProgress(100);
      onChange({
        path,
        signedUrl: signed?.signedUrl ?? "",
        name: file.name,
        size: file.size,
        durationSec: duration,
        thumbnailPath: tErr ? undefined : thumbPath,
        thumbnailSignedUrl: thumbSignedRes?.data?.signedUrl ?? undefined,
      });
      toast.success("動画をアップロードしました");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const remove = async () => {
    const paths: string[] = [];
    if (value?.path) paths.push(value.path);
    if (value?.thumbnailPath) paths.push(value.thumbnailPath);
    if (paths.length) {
      await supabase.storage.from("uploads").remove(paths);
    }
    onChange(undefined);
  };

  return (
    <UploaderShell
      titleFixed="ベースとなる動画"
      hint="人物がはっきり映っている動画を推奨"
      formatHint="MP4 / MOV・最大100MB・最大30秒"
      icon={<Video className="h-6 w-6" />}
      uploading={uploading}
      progress={progress}
      hasValue={!!value}
      accept={VIDEO_TYPES.join(",")}
      onFile={handleFile}
      preview={
        value ? (
          <div className="relative">
            <video
              src={value.signedUrl}
              controls
              className="aspect-video w-full rounded-lg bg-black object-contain"
            />
            <RemoveButton onClick={remove} />
            <FileMeta
              name={value.name}
              size={value.size}
              extra={`${value.durationSec.toFixed(1)}秒`}
            />
          </div>
        ) : null
      }
    />
  );
}

// ---------- Shared UI ----------

function UploaderShell({
  titleFixed,
  hint,
  formatHint,
  icon,
  uploading,
  progress,
  hasValue,
  accept,
  onFile,
  preview,
}: {
  title?: string;
  titleFixed: string;
  hint: string;
  formatHint: string;
  icon: React.ReactNode;
  uploading: boolean;
  progress: number;
  hasValue: boolean;
  accept: string;
  onFile: (f: File) => void;
  preview: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <Card className="flex flex-col p-5">
      <div className="mb-3">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          {icon}
          {titleFixed}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </div>

      {hasValue ? (
        <div>{preview}</div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={handleDrop}
          disabled={uploading}
          className={cn(
            "flex min-h-[220px] w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border p-6 text-center transition-colors",
            drag && "border-primary bg-primary/5",
            !uploading && "hover:border-primary hover:bg-primary/5",
            uploading && "opacity-70",
          )}
        >
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium">アップロード中...</p>
              <Progress value={progress} className="mt-2 w-full max-w-xs" />
              <p className="text-xs text-muted-foreground">{progress}%</p>
            </>
          ) : (
            <>
              <UploadCloud className="h-10 w-10 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  ドラッグ＆ドロップまたは
                  <span className="text-primary">クリックして選択</span>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{formatHint}</p>
              </div>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </Card>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="削除"
      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md transition hover:bg-destructive hover:text-destructive-foreground"
    >
      <X className="h-4 w-4" />
    </button>
  );
}

function FileMeta({ name, size, extra }: { name: string; size: number; extra?: string }) {
  return (
    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
      <span className="truncate">{name}</span>
      <span className="shrink-0">
        {formatBytes(size)}
        {extra ? ` ・ ${extra}` : ""}
      </span>
    </div>
  );
}
